import type { ColumnDef } from "@tanstack/react-table";
import { DataTableColumnHeader, ActionsCell } from "@/components/data-table";
import { CheckCircle2, XCircle, ChevronDown, ChevronUp, Lock } from "lucide-react";
import { msg } from "@lingui/core/macro";
import type { I18n } from "@lingui/core";
import { useLingui } from "@lingui/react/macro";
import type { MaterialRow } from "@/types/material";
import { Badge } from "@/components/ui/badge";
import { formatPackSize } from "@/lib/materials/pack-size";
import { formatMoney } from "@/lib/materials/conversion-calc";
import { canEditMaterial, isSharedCatalogueMaterial } from "@/lib/materials/shared-catalogue";

type EditFn = (material: MaterialRow) => void;
type ToggleFn = (material: MaterialRow) => void;
type DeleteFn = (material: MaterialRow) => void;
type AddSubFn = (material: MaterialRow) => void;

function MaterialAvatar({ name, thumbnailUrl, imageUrl }: { name: string; thumbnailUrl?: string; imageUrl?: string }) {
  const { t } = useLingui();
  const src = thumbnailUrl ?? imageUrl;

  return (
    <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-(--color-surface-muted) overflow-hidden">
      {src ? (
        <img
          src={src}
          alt={name}
          className="size-full object-cover"
          loading="lazy"
        />
      ) : (
        <span className="text-[10px] font-semibold text-(--color-text-secondary)">{t`N/A`}</span>
      )}
    </div>
  );
}

export function getMaterialColumns(
  i18n: I18n,
  onEdit: EditFn,
  onToggle: ToggleFn,
  onDelete: DeleteFn,
  onAddSub: AddSubFn,
  /** Only a super admin may write the shared catalogue — see `canEditMaterial`. */
  isSuperAdmin = false,
): ColumnDef<MaterialRow, unknown>[] {
  return [
    {
      id: "expand",
      enableSorting: false,
      enableHiding: false,
      header: () => null,
      cell: ({ row }) => {
        if (row.depth > 0) return null;
        if (!row.getCanExpand()) return null;
        return (
          <button
            type="button"
            onClick={row.getToggleExpandedHandler()}
            className="flex size-6 items-center justify-center rounded border border-(--shell-border) bg-(--color-surface-content) hover:bg-(--color-surface-muted) transition-colors"
            aria-label={row.getIsExpanded() ? i18n._(msg`Collapse`) : i18n._(msg`Expand`)}
          >
            {row.getIsExpanded()
              ? <ChevronUp className="size-3 text-(--color-text-secondary)" />
              : <ChevronDown className="size-3 text-(--color-text-secondary)" />}
          </button>
        );
      },
      size: 36,
    },
    {
      id: "materialId",
      meta: { label: i18n._(msg`ID`) },
      header: i18n._(msg`ID`),
      cell: ({ row }) => (
        <span className="font-mono text-xs text-(--color-text-secondary)">
          ING-{String(row.original.id).padStart(3, "0")}
        </span>
      ),
      enableSorting: false,
    },
    {
      id: "image",
      meta: { label: i18n._(msg`Image`) },
      header: i18n._(msg`Image`),
      cell: ({ row }) => (
        <MaterialAvatar
          name={row.original.name}
          thumbnailUrl={row.original.thumbnailUrl}
          imageUrl={row.original.imageUrl}
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "name",
      meta: { label: i18n._(msg`Name`) },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n._(msg`Name`)} />
      ),
      cell: ({ row, getValue }) => (
        <span className="flex items-center gap-2">
          <span className="font-medium">{getValue() as string}</span>
          {/* Says why the row has fewer actions than its neighbours: it belongs to
              the shared catalogue, not to this tenant. A super admin sees the
              badge too — they can edit it, and should know who else it affects. */}
          {isSharedCatalogueMaterial(row.original) && (
            <Badge variant="outline" className="gap-1 shrink-0">
              <Lock className="size-3" />
              {i18n._(msg`Shared catalogue`)}
            </Badge>
          )}
        </span>
      ),
    },
    {
      id: "qty",
      meta: { label: i18n._(msg`Qty`) },
      header: i18n._(msg`Qty`),
      cell: ({ row }) => {
        const qty = row.original._subQty;
        if (qty == null) return <span className="text-(--color-text-secondary)">—</span>;
        return <span className="text-sm">{qty}</span>;
      },
      enableSorting: false,
    },
    {
      accessorKey: "unitOfMeasure",
      meta: { label: i18n._(msg`Unit`) },
      header: i18n._(msg`Unit`),
      cell: ({ row }) => {
        // Sub-ingredient rows carry their own unit and have no pack size of their
        // own; only the ingredient itself is bought by the case.
        if (row.original._subUnit != null) {
          return <span className="text-sm">{row.original._subUnit}</span>;
        }
        return <span className="text-sm">{formatPackSize(row.original)}</span>;
      },
      enableSorting: false,
    },
    {
      id: "subRecipe",
      meta: { label: i18n._(msg`Sub-recipe`) },
      header: i18n._(msg`Sub-recipe`),
      cell: ({ row }) => {
        const isCompound = row.original.type === "COMPOUND";
        return (
          <div className="flex items-center gap-1.5">
            {isCompound ? (
              <CheckCircle2 className="size-4 text-(--color-success-text)" />
            ) : (
              <XCircle className="size-4 text-(--color-danger-text)" />
            )}
            <span className={isCompound ? "text-sm text-(--color-success-text)" : "text-sm text-(--color-danger-text)"}>
              {isCompound ? i18n._(msg`Yes`) : i18n._(msg`No`)}
            </span>
          </div>
        );
      },
      enableSorting: false,
    },
    {
      id: "company",
      meta: { label: i18n._(msg`Concept`) },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n._(msg`Concept`)} />
      ),
      cell: ({ row }) => {
        // No concept is not missing data — the ingredient is shared across every
        // concept in the organization, which is what all of them used to be.
        const company = row.original.company;
        if (!company) return <span className="text-(--color-text-secondary)">{i18n._(msg`Shared`)}</span>;
        return <span className="text-sm">{company.name}</span>;
      },
      enableSorting: false,
    },
    {
      accessorKey: "supplier",
      meta: { label: i18n._(msg`Supplier`) },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n._(msg`Supplier`)} />
      ),
      cell: ({ row }) => {
        // Falls back to the legacy free-text name for rows not yet linked to a
        // Supplier record.
        const supplierName = row.original.supplier?.name ?? row.original.supplierName;
        if (!supplierName) return <span className="text-(--color-text-secondary)">—</span>;
        return <span className="text-sm">{supplierName}</span>;
      },
      enableSorting: false,
    },
    {
      accessorKey: "unitCost",
      meta: { label: i18n._(msg`Unit Cost`) },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n._(msg`Unit Cost`)} />
      ),
      // The API derives this from the conversion chart, the price recipe
      // costing uses. Sorting would order by the stored `unitCost` instead.
      cell: ({ row }) => {
        const cost = row.original.effectiveUnitCost;
        if (cost == null) return <span className="text-(--color-text-secondary)">—</span>;
        return (
          <span className="text-sm">
            {formatMoney(cost.value)}/{cost.unit}
          </span>
        );
      },
      enableSorting: false,
    },
    {
      id: "allergens",
      meta: { label: i18n._(msg`Allergens`) },
      header: i18n._(msg`Allergens`),
      cell: ({ row }) => {
        const count = row.original.allergens?.length ?? 0;
        if (count === 0) return <span className="text-(--color-text-secondary)">—</span>;
        return (
          <span className="inline-flex items-center justify-center rounded-full bg-(--color-surface-accent) px-2 py-1 text-xs font-medium text-(--color-text-secondary) min-w-8">
            {count}
          </span>
        );
      },
      enableSorting: false,
    },
    {
      id: "actions",
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => {
        // Every action in this menu writes. On a shared-catalogue row the API
        // refuses all of them, so show no menu at all rather than a trigger that
        // opens onto four things that end in a 403 — the badge on the name says
        // why it is missing. ActionsCell renders an inert button for an empty
        // list, which would read as a broken control, so return nothing.
        if (!canEditMaterial(row.original, isSuperAdmin)) return null;

        return (
          <ActionsCell
            triggerClassName="rounded-[10px] border border-(--shell-border) bg-(--color-surface-content) size-[44px] hover:bg-(--color-surface-muted)"
            items={[
              {
                label: i18n._(msg`Edit`),
                onClick: () => onEdit(row.original),
              },
              ...(row.original.type === "COMPOUND" ? [{
                label: i18n._(msg`Add sub-ingredient`),
                onClick: () => onAddSub(row.original),
              }] : []),
              {
                label: row.original.isActive
                  ? i18n._(msg`Set as inactive`)
                  : i18n._(msg`Set as active`),
                onClick: () => onToggle(row.original),
              },
              {
                label: i18n._(msg`Delete`),
                onClick: () => onDelete(row.original),
                destructive: true,
              },
            ]}
          />
        );
      },
    },
  ];
}
